import { useEffect, useState } from "react";
import { Loader2, Eye } from "lucide-react";
import { api } from "@/lib/api";

export function VisitorsPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/visitors").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  const days = data?.days || [];
  const max = Math.max(...days.map((d) => d.count), 1);
  const last7 = days.slice(0, 7).reverse();

  return (
    <div data-testid="visitors-panel" className="mt-8">
      <div className="flex items-center gap-2">
        <Eye size={18} className="text-slate-400" />
        <h2 className="font-display text-lg font-bold text-slate-900">Visitors</h2>
      </div>

      {data === null ? <div className="mt-4 flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : data.error ? (
        <p className="mt-3 text-sm text-slate-400">Could not load visitor stats.</p>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-3 sm:max-w-md">
            <div data-testid="visitors-today" className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-display text-2xl font-extrabold text-slate-900">{data.today}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Today</p>
            </div>
            <div data-testid="visitors-yesterday" className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-display text-2xl font-extrabold text-slate-900">{data.yesterday}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Yesterday</p>
            </div>
            <div data-testid="visitors-total" className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-display text-2xl font-extrabold text-slate-900">{data.total}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">All Time</p>
            </div>
          </div>

          {last7.length > 0 && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Last 7 days</p>
              <div className="flex h-24 items-end gap-2">
                {last7.map((d) => (
                  <div key={d.date} className="flex flex-1 flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                    <span className="text-[10px] font-bold text-slate-500">{d.count}</span>
                    <div className="w-full rounded-t bg-brand" style={{ height: `${Math.max((d.count / max) * 100, 4)}%` }} />
                    <span className="text-[9px] text-slate-400">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.top_machines?.length > 0 && (
            <div data-testid="top-machines" className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Most Viewed Machines</p>
              <div className="space-y-1.5">
                {data.top_machines.map((t) => (
                  <div key={t.id} data-testid={`top-machine-${t.machine_code}`} className="flex items-center justify-between text-sm">
                    <span className="truncate text-slate-700">{t.name} <span className="font-mono text-xs text-slate-400">{t.machine_code}</span></span>
                    <span className="ml-2 shrink-0 rounded-full bg-brand/10 px-2 py-0.5 text-xs font-bold text-brand">{t.views} view{t.views === 1 ? "" : "s"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
